/**
 * The "Add to Shopping List" control: a button or link that opens a popover of the signed-in
 * user's shopping lists, adds the given products to the list the user picks, or creates a list
 * and adds them to it. Rendered on product details, catalog rows and the catalog bulk menu,
 * search results, cart rows and the cart bulk menu, order details, open orders and the
 * shopping-list details subheader.
 *
 * Position: the list calls go through `components/Helper/shoppingListHelper.ts`
 * (`getShoppingList`, `createShoppingList`, `addProductsToShoppingList`) to the Shopping API
 * (`APIUrl.SHOPPING_LIST`, `web-shopping-api`). The request's zip code comes from
 * `components/AccountSettings/Helpers/zipCode.ts` → `getZipCode`. Below the `lg` breakpoint,
 * pages with a bulk selection use `BulkAddToShoppingListMobile.tsx` instead.
 *
 * Behaviour a change must keep in mind:
 * - The lists are fetched each time the popover opens, and only for a signed-in user.
 * - Before opening, `onClickPopover` refuses the products when any line has `quantity` 0 or a
 *   `cutFlag` of `DefineCut.CutIsAlwaysRequired` ("A"). It reads `cutFlag` only, not
 *   `processing`, and callers pass the product's catalog flag, so a cut-required product is
 *   refused even with a cut defined. That refusal is a known defect: "a product with its cut
 *   defined must be accepted". A signed-in user gets `handleOnError("addToShoppingList")` when
 *   the caller passes it; a guest gets nothing. A guest with valid products triggers
 *   `handleUnauthorized`; callers on signed-in-only pages pass a no-op.
 * - `Popover` (`@ryerson/frontend.modal`) also toggles itself on any click inside its trigger.
 *   The Link trigger stops propagation; the Button trigger does not.
 * - Header PO: only product details passes `lineLevelInfo` (read from the page's URL in
 *   `components/Product/Context/ProductDetailContext.tsx` → `lineLevelInfo`). When it carries a
 *   `headerPurchaseOrder` and the chosen list or the user's profile already has a PO, the add is
 *   handed to that page's replace-PO prompt (`setDisplayPOReplacePrompt` / `setReplacePOnumber`;
 *   `ProductDetailContext.tsx` → `addProductToShoppingList` performs it). Otherwise this component
 *   adds the items and then writes the header PO onto the list.
 * - New list: a desktop modal, or the mobile `SliderMenu` unless `hideSliderMenu`.
 *   `handleNewShoppingListClicked` replaces both (order details brings its own form in
 *   `components/OrderDetails/Helpers/OrderProductTableHeader.tsx`). The desktop Create List button
 *   is disabled while the create call runs; the mobile one is not.
 *
 * Also exports styled pieces reused by `OrderProductTableHeader.tsx`, and the {@link ShoppingList}
 * type used by `BulkAddToShoppingListMobile.tsx` and `ProductDetailContext.tsx`.
 *
 * Legacy call style: the PO update is an inline `axiosInstance.put` on an `APIUrl` path. New calls
 * take the URL and types from `@ryerson/common.service-contracts`.
 */
import React, { createRef, RefObject, useEffect, useState } from "react";
import { useApplication } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
import { Button } from "@ryerson/frontend.button";
import { Popover, PopperExposedHandlers } from "@ryerson/frontend.modal";
import { Alert } from "@ryerson/frontend.notification";
import styled from "@emotion/styled";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import { Input } from "@ryerson/frontend.form-controls";
import { AddToShoppingListContent } from "./AddToShoppingListContent";
import { Media } from "@ryerson/frontend.next";
import { SliderMenu } from "@components/Helper/commonStyles";
import { isAxiosError, useWindowSize } from "@ryerson/frontend.common";
import { ModalContainer } from "@components/ShoppingListDashboard/Helpers/StyledComponents";
import { Icon } from "@ryerson/frontend.assets";
import { DefineCut } from "@ryerson/frontend.common";
import {
	addProductsToShoppingList,
	AjaxCreateShoppingListResponse,
	AjaxGetShoppingListResponse,
	createShoppingList,
	CreateShoppingListType,
	GetShoppingList,
	getShoppingList,
	ShoppingListItemsRequest,
	ShoppingListProduct,
} from "@components/Helper/shoppingListHelper";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { ReplacePoNumberList } from "@components/Product/Context/ProductDetailContext";
import { AxiosError, AxiosResponse } from "axios";
import { LineLevelPO } from "@components/Product/ProductDetails";
import { APIUrl } from "@enums/apiUrl.enums";
import { IError } from "@ryerson/common.models";
import { TranslatedText, useTranslation } from "@components/Shared/TranslationContext";

/** Trigger style: an outline button with a chevron, or an icon plus text link. */
export type Control = "Button" | "Link";
/**
 * For the Button trigger, the background: `white` is the theme white, `clear` is transparent.
 * For the Link trigger, the icon and text colours: brand colour on `white`; light gray icon and
 * white text on `clear`.
 */
export type Background = "clear" | "white";
/** Trigger size, passed to the button or the link text. */
export type Size = "sm" | "md" | "lg";
interface AddToShoppingListProps {
	dataTestId: string;
	control: Control; // default Button
	/**
	 * Button trigger: the background. Link trigger: the icon and text colours. See
	 * {@link Background}. The component's default is `"white"`.
	 */
	background?: Background; // default primary
	buttonColor?: string;
	/** Label reads "Add Selected to Shopping List" instead of "Add to Shopping List". */
	labelWithSelectedText?: Boolean;
	size: Size;
	iconSize?: Size; // Only for Control as Link
	/** The lines to add, in the Shopping API shape. Validated on open; see the file header. */
	products: ShoppingListProduct[];
	/** Called when a guest opens the control with valid products. */
	handleUnauthorized: () => void;
	/** Called with the add-items response `data` after items reach a list. */
	addToShoppingList: (addedShoppingListResponse: any) => void;
	/**
	 * Called with `"addToShoppingList"` when a signed-in user opens the control with invalid
	 * products.
	 */
	handleOnError?: (button?: "addToShoppingList" | "saveAsQuote", message?: string) => void;
	expandRowId?: string; // className of containing div around expandContent row for table
	/** Accepted but not read: the popover uses a fixed `offset={[0, 5]}`. */
	popoverOffset?: string;
	/** Text type of the success alert. */
	messageType?: "primary" | "secondary" | "tertiary";
	/**
	 * Hides this component's success alert. Passed by the bulk action menus, which show their own
	 * success message: catalog (`BulkActionItem.tsx`), cart (`useShoppingCartActionItems.ts`) and
	 * list details (`ShoppingListSubheaderDesktop.tsx`).
	 */
	bulk?: boolean;
	disabled?: boolean;
	/** Skips the mobile create-list slider; for callers that bring their own create form. */
	hideSliderMenu?: boolean;
	/** Replaces the built-in create-list form when "+ New Shopping List" is clicked. */
	handleNewShoppingListClicked?: () => void;
	/** Product details only: opens that page's replace-PO prompt. */
	setDisplayPOReplacePrompt?: React.Dispatch<React.SetStateAction<boolean>> | null;
	/**
	 * Product details only: hands the chosen list and success callback to the replace-PO
	 * prompt.
	 */
	setReplacePOnumber?: React.Dispatch<React.SetStateAction<ReplacePoNumberList>>;
	/** Product details only: the order-line PO fields from the page URL. */
	lineLevelInfo?: LineLevelPO;
	/** Called when the popover opens for a signed-in user, unless `disabled`. */
	clearErrors?: () => void;
}

/**
 * A list as the list pickers and the replace-PO prompt hold it. Nothing sets or reads
 * `permissionName`.
 */
export type ShoppingList = {
	permissionName?: string;
	shoppingListId: number;
	name: string;
};

const AddToShoppingListButton = styled(Button)`
	${(props: any) => {
		return ` 
		  
			background-color: ${props.backgroundColor};
		`;
	}};
`;

const Divider = styled.div`
	margin: 20px 0;
	opacity: 0.1;
	${(props: any) => {
		const { color } = props;
		return `
			border-top: 1px solid ${color};
		`;
	}};
`;

const ShoppingListMenu = styled(Flex)`
	${(props: any) => {
		return `
			background-color: ${props.background};
			padding: 20px;
			border-radius: 2px; 
			box-shadow: 0px 100px 80px rgba(0, 0, 0, 0.07), 0px 0px 17.869px rgba(0, 0, 0, 0.0417275),
				0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725);

			width: ${props?.width ? props?.width + "px" : "100% "};
		 
	 
			@media only screen and (max-width: calc( ${props.breakpoint} - 1px )) {
				margin-right: 0px;
				
			}
		`;
	}};
`;

const ShoppingListMenuItem = styled(FlexItem)`
	margin: 5px 0;
	width: 100%;
`;

/** Full-width body of the mobile create-list slider. Also used by `OrderProductTableHeader.tsx`. */
export const ModalContentMobile = styled(Flex)`
	width: 100vw;
	overflow: auto;
	bottom: 0;

	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;

/** Padded inner area of {@link ModalContentMobile}. */
export const ModalContentMobileInner = styled.div`
	padding: 20px;
`;

const ShoppingListsContainer = styled.div`
	max-height: 120px;
	overflow-y: auto;
`;

const ShoppingListsItem = styled(Flex)`
	margin: 10px 0;
	cursor: pointer;
	${(props: any) => {
		return `
			&:hover span {
				color: ${props.color};
			}
		`;
	}};
`;

/** Top bar of the mobile create-list slider; holds the close button at the right. */
export const CloseModalButtonOuterContainer = styled(Flex)`
	width: 100%;
	box-sizing: border-box;

	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;

/** 50px square cell for the close button, with a left border from `borderSpecs`. */
export const CloseModalButtonInnerContainer = styled(Flex)`
	width: 50px;
	height: 50px;
	box-sizing: border-box;

	${(props: any) => {
		const { borderSpecs } = props;
		return css`
			border-left: ${borderSpecs};
		`;
	}};
`;

/** Close button with a white icon. */
export const CloseButton = styled(Button)`
	span {
		color: white;
	}
`;

/** Wrapper of the "Added to Shopping List!" success alert. */
export const AlertContainer = styled.div`
	margin-left: -10px;
`;

/**
 * Renders the trigger, the list popover, the success alert (5 s, unless `bulk`) and the
 * create-list form (desktop modal at `lg` and up, mobile slider below). See the file header for
 * validation and the header-PO flow.
 */
const AddToShoppingList: React.FC<AddToShoppingListProps> = ({
	dataTestId,
	background = "white",
	size = "sm",
	iconSize = "sm",
	control = "Button" as Control,
	buttonColor,
	labelWithSelectedText = false,
	products,
	handleUnauthorized,
	handleOnError,
	addToShoppingList,
	popoverOffset = "56px",
	messageType = "tertiary",
	bulk = false,
	disabled = false,
	expandRowId,
	hideSliderMenu = false,
	handleNewShoppingListClicked,
	setDisplayPOReplacePrompt,
	setReplacePOnumber,
	lineLevelInfo,
	clearErrors,
}) => {
	const { profile } = useApplication();
	const { theme } = useTheme();
	const { white, darkGray, gray, primaryBrand, lightGray } = theme.colors.primary;
	const {
		axiosInstance,
		localization: { language },
		user: { isLoggedIn },
		isNative,
		zipCode,
	} = useApplication();
	const { translations } = useTranslation();

	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [isShoppingListExpanded, setIsShoppingListExpanded] = useState<boolean>(false);
	const [isNewShoppingListModalOpen, setIsNewShoppingListModalOpen] = useState<boolean>(false);
	const [newListName, setNewListName] = useState<string>("");
	const [shoppingLists, setShoppingLists] = useState<GetShoppingList[]>([]);
	const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
	const [domNodeRefs] = useState<any>({});
	const windowSize = useWindowSize();
	const { salesPlant, customerPickup, selectedShipToAddress } = useEcommerce();
	const [isDisabled, setIsDisabled] = useState<boolean>(false);
	const [menuWidth, setMenuWidth] = useState<number>();

	const content = AddToShoppingListContent[language] || AddToShoppingListContent.en;
	const btnColor = buttonColor ? buttonColor : white;

	const requestBody: ShoppingListItemsRequest = {
		zipCode: getZipCode(isLoggedIn, zipCode, selectedShipToAddress, customerPickup, salesPlant),
		items: products,
	};

	// This is used to pass to forwarded ref in order to expose the popper instance and its methods
	const popperRef = React.useRef<PopperExposedHandlers | null>(null);

	// With `expandRowId`, toggles the expanded row's overflow with the popover (see
	// handlePopOverWasToggled). On open, for a signed-in user: clears errors unless `disabled`,
	// and re-fetches the lists.
	useEffect(() => {
		!!expandRowId && handlePopOverWasToggled(isShoppingListExpanded);

		if (isLoggedIn && isShoppingListExpanded) {
			if (!disabled) clearErrors && clearErrors();
			getShoppingList(isLoggedIn, axiosInstance, handleOnSuccessGetShoppingList);
		}
	}, [isShoppingListExpanded]);

	useEffect(() => {
		if (showSuccessMessage) {
			const timer = setTimeout(() => {
				setShowSuccessMessage(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [showSuccessMessage]);

	// Manage slider animation and body scrolling
	// Below `lg` only. Closing clears the body's whole inline style, not only `overflow`.
	useEffect(() => {
		if (hasMounted && windowSize[0] < parseInt(theme.breakpoints.lg)) {
			const sliderEl = domNodeRefs["add-to-shopping-list-modal"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");
			if (isNewShoppingListModalOpen) {
				(window as any).scrollTo(0, 0);
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 300);
			} else {
				body && body.setAttribute("style", "");
				setIsNewShoppingListModalOpen(false);
			}
		}
	}, [isNewShoppingListModalOpen]);

	useEffect(() => {
		setHasMounted(true);
		setIsNewShoppingListModalOpen(false);
	}, []);

	/**
	 * Validates `products` and calls `popperRef.open()` only for a signed-in user with valid
	 * products. Invalid products (a zero quantity, or `cutFlag` "A" whatever the `processing`)
	 * call `handleOnError` instead, or nothing for a guest. Refusing a cut-required product whose
	 * cut is defined is a known defect (see the file header). With no products, or `expand` false,
	 * it closes the popover. The Popover's own click toggle runs in addition (see the file header).
	 */
	const onClickPopover = (expand: boolean) => {
		if (products.length > 0 && expand === true) {
			let shoppingListWithInvalidQty = products.filter((product) => product.quantity === 0);

			let shoppingListWithMissingCut = products.filter(
				(product) => product.cutFlag === DefineCut.CutIsAlwaysRequired
			);

			let hasValidShoppingList =
				shoppingListWithInvalidQty.length === 0 && shoppingListWithMissingCut.length === 0;

			if (isLoggedIn) {
				if (!hasValidShoppingList) {
					handleOnError && handleOnError("addToShoppingList");
				} else {
					popperRef.current?.open();
				}
			} else if (hasValidShoppingList) {
				handleUnauthorized();
			}
		} else {
			setIsShoppingListExpanded(false);
			popperRef.current?.close();
		}
	};

	const addNewShoppingList = () => {
		if (isLoggedIn) {
			onClickPopover(false);
			setIsNewShoppingListModalOpen(true);
		}
	};

	const createNewShoppingList = async () => {
		if (isLoggedIn && newListName.length > 0) {
			// Disables the desktop Create List button until the call ends; the mobile button
			// does not read isDisabled.
			setIsDisabled(true);
			const body = {
				name: newListName,
			};
			await createShoppingList(
				isLoggedIn,
				axiosInstance,
				body,
				handleOnSuccessCreateShoppingList,
				() => {
					setIsDisabled(false);
				}
			);
		}
	};

	const handleOnSuccessCreateShoppingList = (response: AjaxCreateShoppingListResponse) => {
		const responseData = response?.data;
		if (responseData) {
			// Empty the shoppingLists since we’re closing the popper.
			setShoppingLists([]);
			setNewListName("");
			setIsNewShoppingListModalOpen(false);
			setIsShoppingListExpanded(false);
			setIsDisabled(false);
			popperRef.current?.close();

			// A new list has no PO, so only the profile's PO can trigger the replace-PO prompt.
			if (
				!!lineLevelInfo?.headerPurchaseOrder &&
				!!profile.settings.purchaseOrderNumber &&
				setDisplayPOReplacePrompt &&
				setReplacePOnumber
			) {
				setDisplayPOReplacePrompt(true);
				setReplacePOnumber({
					replace: false,
					shoppingList: responseData,
					handleOnSuccessAddToList,
				});
			} else {
				addProductsToShoppingList(
					requestBody,
					responseData.shoppingListId,
					isLoggedIn,
					axiosInstance,
					(res) => handleOnSuccessAddToList(res, responseData)
				);
			}
		}
	};

	const handleOnSuccessAddToList = (
		response: any,
		shoppingList?: CreateShoppingListType | GetShoppingList
	) => {
		const responseData = response?.data;
		if (responseData) {
			/**
			 * If lineLevelInfo is passed and there are no PO on the list or in the profile,
			 * just add the {@linkcode lineLevelInfo?.headerPurchaseOrder} to the shopping list directly.
			 * `shoppingList` is passed only on the direct-add paths; after the replace-PO prompt
			 * the page's context writes the PO, if the user chose to replace it. The PUT resends
			 * the list's current name, and a failure is only logged: the success alert still shows.
			 */
			if (!!lineLevelInfo?.headerPurchaseOrder && !!shoppingList) {
				axiosInstance
					.put<null, AxiosResponse<null> | AxiosError<IError>>(
						`${APIUrl.SHOPPING_LIST}/${shoppingList.shoppingListId}`,
						{
							purchaseOrderNumber: lineLevelInfo.headerPurchaseOrder,
							name: shoppingList.name,
						}
					)
					.then((res) => {
						if (isAxiosError(res)) throw res;
					})
					.catch((err) => {
						console.warn(err);
					});
			}
			setShowSuccessMessage(true);
			addToShoppingList(responseData);
		}
	};

	const handleOnSuccessGetShoppingList = (response: AjaxGetShoppingListResponse) => {
		const responseData = response?.data;
		if (responseData) {
			setShoppingLists([...responseData]);
		}
	};

	const handleListNameChange = (event: any) => {
		setNewListName(event.target.value);
	};

	/**
	 * Sets the parent of the `expandRowId` element to `overflow: visible` while the popover is
	 * open, so the popover can extend past the expanded table row, and back to `hidden` on close.
	 */
	const handlePopOverWasToggled = (isOpen: boolean) => {
		const parentEl = document.querySelector(`.${expandRowId}`)?.parentElement;
		isOpen && parentEl && (parentEl.style["overflow"] = "visible");
		!isOpen && parentEl && (parentEl.style["overflow"] = "hidden");
	};

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	const getModalContentDesktop = () => {
		return (
			<ModalContainer
				title={content.createNewList}
				onClose={() => {
					setIsNewShoppingListModalOpen(false);
				}}
				isOpen={isNewShoppingListModalOpen}
				buttonArea={
					<Flex
						justifyContent="flex-start"
						style={css`
							width: 100%;
						`}
					>
						<Button
							label={content.createList}
							onClick={createNewShoppingList}
							fullwidth={false}
							disabled={newListName.length < 1 || isDisabled}
						/>
					</Flex>
				}
			>
				<Flex direction="column" css={{ marginTop: "20px", width: "500px" }}>
					<FlexItem css={{ marginBottom: 11 }}>
						<Input
							label={content.listName}
							value={newListName}
							required={true}
							onChange={handleListNameChange}
							type="secondary"
							language={language}
						/>
					</FlexItem>
					<FlexItem css={{ marginBottom: 15 }}>
						<Typography size="sm" autoResize={true} color={gray}>
							<TranslatedText text={translations.newShoppingListSubText} />
						</Typography>
					</FlexItem>
				</Flex>
			</ModalContainer>
		);
	};

	const getModalContentMobile = () => {
		return (
			<ModalContentMobile
				direction="column"
				css={{ height: windowSize[1] - 100, overflowY: "scroll" }}
			>
				<CloseModalButtonOuterContainer
					{...{ backgroundColor: darkGray }}
					justifyContent="flex-end"
					alignItems="center"
				>
					<CloseModalButtonInnerContainer
						{...{ borderSpecs: `1px solid ${gray}` }}
						justifyContent="center"
						alignItems="center"
					>
						<CloseButton
							background={darkGray}
							shape="square"
							leftIcon="close"
							label=""
							onClick={() => {
								setIsNewShoppingListModalOpen(false);
							}}
							size="sm"
						/>
					</CloseModalButtonInnerContainer>
				</CloseModalButtonOuterContainer>
				<ModalContentMobileInner>
					<Typography variant="h2">{content.createNewList}</Typography>
					<Input
						label={content.listName}
						value={newListName}
						required={true}
						onChange={handleListNameChange}
						type="secondary"
						language={language}
						css={{ marginTop: "15px" }}
					/>
					<Typography variant="p">
						<TranslatedText text={translations.newShoppingListSubText} />
					</Typography>

					<Flex
						justifyContent="flex-start"
						style={css`
							width: 100%;
						`}
					>
						<FlexItem
							style={css`
								width: 100%;
							`}
						>
							<Button
								label={content.createList}
								onClick={createNewShoppingList}
								fullwidth={true}
								disabled={newListName.length < 1}
							/>
						</FlexItem>
					</Flex>
				</ModalContentMobileInner>
			</ModalContentMobile>
		);
	};

	const getWidthOfTarget = (e: React.MouseEvent, offset: number = 0) => {
		let widthNum = parseInt(e?.currentTarget?.getBoundingClientRect().width?.toFixed(0));
		return !!widthNum ? widthNum - offset : 0;
	};
	const getPopoverTrigger = (
		controlType: Control,
		background: Background,
		size: Size
	): JSX.Element => {
		if (controlType === "Button") {
			return (
				<AddToShoppingListButton
					leftIcon="shopping-list"
					rightIcon="chevron-down"
					label={
						labelWithSelectedText
							? content.addSelectedToShoppingList
							: content.addToShoppingList
					}
					onClick={(e) => {
						let event = e as unknown as React.MouseEvent;
						setMenuWidth(getWidthOfTarget(event, 4));
						onClickPopover(!isShoppingListExpanded);
					}}
					fullwidth={true}
					align="space"
					variant="outline"
					background={btnColor}
					size={size}
					type={"secondary"}
					disabled={disabled}
					dataTestId={dataTestId}
					{...{ backgroundColor: background === "white" ? white : "transparent" }}
				/>
			);
		} else {
			return (
				<Flex
					alignItems="center"
					onClick={(e) => {
						e.stopPropagation();
						onClickPopover(!isShoppingListExpanded);
					}}
					css={{ cursor: "pointer" }}
				>
					<Icon
						color={background === "white" ? primaryBrand : lightGray}
						icon="shopping-list"
						size={iconSize}
						css={{ marginRight: 10, cursor: "pointer" }}
					/>
					<Typography size={size} color={background === "white" ? primaryBrand : white}>
						{labelWithSelectedText
							? content.addSelectedToShoppingList
							: content.addToShoppingList}
					</Typography>
				</Flex>
			);
		}
	};

	return (
		<>
			<Media greaterThanOrEqual="lg">{getModalContentDesktop()}</Media>

			<Popover
				ref={popperRef}
				caretColor={theme.colors.primary.background}
				trigger={getPopoverTrigger(control, background, size)}
				alignment="bottom"
				offset={[0, 5]}
				onOpen={() => {
					setIsShoppingListExpanded(true);
				}}
				onClose={() => {
					setIsShoppingListExpanded(false);
				}}
				disabled={disabled}
				dataTestId={dataTestId}
			>
				<ShoppingListMenu
					{...{
						breakpoint: theme.breakpoints.lg,
						background: theme.colors.primary.background,
						width: menuWidth,
					}}
				>
					<ShoppingListMenuItem>
						<Button
							size={"sm"}
							fullwidth={true}
							shape="rounded"
							foreground={"light"}
							label={content.newShoppingList}
							onClick={() => {
								typeof handleNewShoppingListClicked !== "undefined"
									? handleNewShoppingListClicked()
									: addNewShoppingList();
							}}
							background={darkGray}
							dataTestId="add-to-shopping-list-add-button"
						/>
						<Divider color={gray} />
						<ShoppingListsContainer>
							{shoppingLists.map((shoppingList, index) => {
								return (
									<ShoppingListsItem
										justifyContent="space-between"
										alignItems="center"
										key={index}
										{...{ color: primaryBrand }}
										onClick={() => {
											/**
											 * If the page has a header PO
											 * (`lineLevelInfo.headerPurchaseOrder`), passed both
											 * replace-PO setters, and there is already a PO (in
											 * profile or on the list),
											 * then display the prompt about replacing PO number.
											 * otherwise, addProductsToShoppingList
											 */
											if (
												!!lineLevelInfo?.headerPurchaseOrder &&
												(!!shoppingList.purchaseOrderNumber ||
													!!profile.settings.purchaseOrderNumber) &&
												setDisplayPOReplacePrompt &&
												setReplacePOnumber
											) {
												setDisplayPOReplacePrompt(true);
												setReplacePOnumber({
													replace: false,
													shoppingList,
													handleOnSuccessAddToList: (response: any) => {
														handleOnSuccessAddToList(response);
													},
												});
											} else {
												addProductsToShoppingList(
													requestBody,
													shoppingList.shoppingListId,
													isLoggedIn,
													axiosInstance,
													(res) =>
														handleOnSuccessAddToList(res, shoppingList)
												);
											}
											setIsShoppingListExpanded(false);
											popperRef.current?.close();
										}}
									>
										<FlexItem>
											<Typography size="sm">{shoppingList.name}</Typography>
										</FlexItem>
									</ShoppingListsItem>
								);
							})}
						</ShoppingListsContainer>
					</ShoppingListMenuItem>
				</ShoppingListMenu>
			</Popover>

			{showSuccessMessage && !bulk && (
				<AlertContainer>
					<Alert severity={"success"}>
						<Typography type={messageType} size="sm">
							{content.successMessage}
						</Typography>
					</Alert>
				</AlertContainer>
			)}
			{!hideSliderMenu ? (
				<Media lessThan="lg">
					<SliderMenu
						{...{ theme: theme }}
						ref={creatDomNodeRef(`add-to-shopping-list-modal`)}
						className="add-to-shopping-list-modal slide-close"
						css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
					>
						{getModalContentMobile()}
					</SliderMenu>
				</Media>
			) : null}
		</>
	);
};

export default AddToShoppingList;
